import { useCallback, useEffect, useRef } from 'react';
import { ACTIVITY_DESCRIPTION_MAX_LENGTH, ACTIVITY_TEXT_MAX_LENGTH } from '@sparkapp/shared-types';
import type { ActivityFormData, ActivityPointForm } from '../../types/activity';
import type { ActivityFieldErrors } from './validateActivityForm';
import { useDomains } from '../../hooks/useDomains';
import { useDomainSkills } from '../../hooks/useDomainSkills';
import { activityUploadApi } from '../../services/activityApi';
import PineSelect from '../spark-activities/PineSelect';
import SkillsCombobox from '../spark-activities/SkillsCombobox';
import ActivityImageUpload from '../spark-activities/ActivityImageUpload';
import DifficultyLevels from './DifficultyLevels';
import ActivitySharedTags from './ActivitySharedTags';
import ActivityImagePlaceholder from './ActivityImagePlaceholder';

export interface ActivityImageValue {
  documentId: number;
  viewUrl: string;
}

/** Scalar / id-list fields edited directly (levels go through onLevelChange) */
export type ActivityFormField = Exclude<keyof ActivityFormData, 'levels'>;

interface ActivityFormFieldsProps {
  formData: ActivityFormData;
  onFieldChange: <K extends ActivityFormField>(field: K, value: ActivityFormData[K]) => void;
  onLevelChange: (level: number, points: ActivityPointForm[]) => void;
  errors: ActivityFieldErrors;
  imageValue: ActivityImageValue | null;
  onImageChange: (image: ActivityImageValue | null) => void;
  readOnly?: boolean;
}

function FieldLabel({ label, hint, required }: { label: string; hint?: string; required?: boolean }) {
  return (
    <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-pine-pine mb-[6px]">
      {label}
      {required && <span className="text-pine-danger"> *</span>}
      {hint && <span className="ml-1.5 font-normal normal-case tracking-normal text-pine-ink-soft">{hint}</span>}
    </p>
  );
}

function FieldError({ message }: { message?: string }) {
  return message ? <p className="text-[11px] text-pine-danger mt-1">{message}</p> : null;
}

const textareaClass = (hasError: boolean) =>
  `w-full border-[1.5px] rounded-[9px] p-[9px_10px] text-[13px] leading-[1.5] bg-white resize-none focus:outline-none transition-colors disabled:bg-pine-paper disabled:text-pine-ink-soft ${
    hasError ? 'border-pine-danger' : 'border-pine-line hover:border-pine-pine focus:border-pine-pine'
  }`;

export default function ActivityFormFields({
  formData,
  onFieldChange,
  onLevelChange,
  errors,
  imageValue,
  onImageChange,
  readOnly = false,
}: ActivityFormFieldsProps) {
  const { data: domains = [] } = useDomains();
  const { data: skills = [] } = useDomainSkills();

  const descRef = useRef<HTMLTextAreaElement>(null);
  const materialsRef = useRef<HTMLTextAreaElement>(null);
  const whyRef = useRef<HTMLTextAreaElement>(null);

  const autoResize = useCallback((el: HTMLTextAreaElement | null) => {
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, []);

  // Fit textareas to content when a different activity is loaded
  useEffect(() => {
    autoResize(descRef.current);
    autoResize(materialsRef.current);
    autoResize(whyRef.current);
  }, [formData.description, formData.materialsNeeded, formData.whyItWorks, autoResize]);

  const descriptionLength = formData.description.trim().length;
  const descriptionTooLong = descriptionLength > ACTIVITY_DESCRIPTION_MAX_LENGTH;
  const lockedClass = readOnly ? 'pointer-events-none opacity-70' : '';

  return (
    <div data-testid="activity-form-fields">
      {/* Title */}
      <input
        data-testid="activity-title-input"
        type="text"
        value={formData.title}
        onChange={(e) => onFieldChange('title', e.target.value)}
        placeholder="Activity title"
        maxLength={255}
        disabled={readOnly}
        className={`w-full font-display text-[31px] font-semibold bg-transparent border-b-2 outline-none transition-colors disabled:text-pine-ink
          ${errors.title ? 'border-pine-danger' : 'border-transparent hover:border-pine-line focus:border-pine-pine'}`}
      />
      <FieldError message={errors.title} />

      {/* Two-column section */}
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_auto] gap-[22px] mt-[26px]">
        <div className="flex flex-col gap-[18px] min-w-0">
          <div>
            <FieldLabel label="Preview description" hint={`(max ${ACTIVITY_DESCRIPTION_MAX_LENGTH} characters)`} required />
            <textarea
              ref={descRef}
              data-testid="activity-description-input"
              value={formData.description}
              onChange={(e) => {
                autoResize(e.target);
                onFieldChange('description', e.target.value);
              }}
              disabled={readOnly}
              rows={2}
              placeholder="A short summary shown on the activity card"
              className={textareaClass(Boolean(errors.description) || descriptionTooLong)}
            />
            <div className="flex items-start justify-between gap-2">
              <FieldError message={errors.description} />
              <span
                className={`ml-auto text-[11px] tabular-nums mt-1 ${
                  descriptionTooLong ? 'text-pine-danger font-semibold' : 'text-pine-ink-soft'
                }`}
              >
                {descriptionLength} / {ACTIVITY_DESCRIPTION_MAX_LENGTH}
              </span>
            </div>
          </div>

          <div>
            <FieldLabel label="What you'll need" hint="(one item per line)" />
            <textarea
              ref={materialsRef}
              data-testid="activity-materials-input"
              value={formData.materialsNeeded}
              onChange={(e) => {
                autoResize(e.target);
                onFieldChange('materialsNeeded', e.target.value);
              }}
              disabled={readOnly}
              rows={2}
              maxLength={ACTIVITY_TEXT_MAX_LENGTH}
              placeholder="e.g. A soft ball"
              className={textareaClass(Boolean(errors.materialsNeeded))}
            />
            <FieldError message={errors.materialsNeeded} />
          </div>

          <div data-testid="domain-select" className={lockedClass}>
            <FieldLabel label="Domain" required />
            <PineSelect
              options={[
                { value: '', label: 'Select domain…' },
                ...domains.map((d) => ({ value: d.id, label: d.title })),
              ]}
              value={formData.domainId ?? ''}
              onChange={(v) => onFieldChange('domainId', v === '' ? null : Number(v))}
              placeholder="Select domain…"
              error={Boolean(errors.domainId)}
            />
            <FieldError message={errors.domainId} />
          </div>

          <div className={lockedClass}>
            <FieldLabel label="Skills targeted" hint="(add all that apply)" required />
            <SkillsCombobox
              skills={skills}
              selectedIds={formData.skillIds}
              onChange={(ids) => onFieldChange('skillIds', ids)}
              error={errors.skillIds}
              hideLabel
            />
          </div>

          <div>
            <FieldLabel label="Why it works" required />
            <textarea
              ref={whyRef}
              data-testid="activity-why-input"
              value={formData.whyItWorks}
              onChange={(e) => {
                autoResize(e.target);
                onFieldChange('whyItWorks', e.target.value);
              }}
              disabled={readOnly}
              rows={3}
              maxLength={ACTIVITY_TEXT_MAX_LENGTH}
              placeholder="Explain the developmental benefit…"
              className={textareaClass(Boolean(errors.whyItWorks))}
            />
            <FieldError message={errors.whyItWorks} />
          </div>
        </div>

        <div data-testid="activity-image-section" className="lg:w-[176px]">
          <FieldLabel label="Activity image" />
          {readOnly ? (
            imageValue ? (
              <img
                src={imageValue.viewUrl}
                alt="Activity"
                className="w-[176px] h-[176px] object-contain rounded-[12px] border border-pine-line bg-pine-paper"
              />
            ) : (
              <ActivityImagePlaceholder />
            )
          ) : (
            <>
              <ActivityImageUpload value={imageValue} onChange={onImageChange} uploadApi={activityUploadApi} />
              {imageValue && (
                <button
                  type="button"
                  data-testid="remove-image-button"
                  onClick={() => onImageChange(null)}
                  className="mt-1 text-xs text-pine-danger hover:underline"
                >
                  Remove image
                </button>
              )}
              <p className="text-xs text-pine-ink-soft mt-1">Optional — the app shows a default image when empty</p>
            </>
          )}
          <FieldError message={errors.imageDocumentId} />
        </div>
      </div>

      {/* The activity */}
      <div className="mt-[26px]">
        <DifficultyLevels levels={formData.levels} onChange={onLevelChange} errors={errors} readOnly={readOnly} />
      </div>

      {/* Shared tags */}
      <div className="mt-[26px]">
        <ActivitySharedTags
          playTypeIds={formData.playTypeIds}
          preferenceIds={formData.preferenceIds}
          physicalDeckIds={formData.physicalDeckIds}
          onChange={(field, ids) => onFieldChange(field, ids)}
          readOnly={readOnly}
        />
      </div>

      <FieldError message={errors._form} />
    </div>
  );
}
